/**
 * Hook pour récupérer la configuration UI payroll par pays.
 *
 * IMPORTANT: Source de vérité = Backend
 * Ce hook utilise un fallback temporaire Tunisia en attendant l'API backend.
 */

import { useState, useEffect } from "react";
import { usePayrollCountry } from "./usePayrollCountry";
import { TUNISIA_DEFAULTS } from "../lib/constants";
import { apiClient } from "../services/api";

/**
 * Génère des ranges de salaires basés sur la devise
 */
const generateSalaryRanges = (currency) => {
  switch (currency) {
    case "TND":
      return [
        { label: "< 1000 DT", min: 0, max: 1000 },
        { label: "1000-2000 DT", min: 1000, max: 2000 },
        { label: "2000-3000 DT", min: 2000, max: 3000 },
        { label: "3000-5000 DT", min: 3000, max: 5000 },
        { label: "> 5000 DT", min: 5000, max: Infinity },
      ];
    case "EUR":
      return [
        { label: "< 2000 €", min: 0, max: 2000 },
        { label: "2000-3000 €", min: 2000, max: 3000 },
        { label: "3000-4000 €", min: 3000, max: 4000 },
        { label: "4000-6000 €", min: 4000, max: 6000 },
        { label: "> 6000 €", min: 6000, max: Infinity },
      ];
    case "MAD":
      return [
        { label: "< 5000 DH", min: 0, max: 5000 },
        { label: "5000-10000 DH", min: 5000, max: 10000 },
        { label: "10000-15000 DH", min: 10000, max: 15000 },
        { label: "15000-25000 DH", min: 15000, max: 25000 },
        { label: "> 25000 DH", min: 25000, max: Infinity },
      ];
    case "QAR":
    case "SAR":
      return [
        { label: `< 5000 ${currency}`, min: 0, max: 5000 },
        { label: `5000-10000 ${currency}`, min: 5000, max: 10000 },
        { label: `10000-20000 ${currency}`, min: 10000, max: 20000 },
        { label: `20000-30000 ${currency}`, min: 20000, max: 30000 },
        { label: `> 30000 ${currency}`, min: 30000, max: Infinity },
      ];
    default:
      // Fallback générique
      return [
        { label: "< 1000", min: 0, max: 1000 },
        { label: "1000-2000", min: 1000, max: 2000 },
        { label: "2000-5000", min: 2000, max: 5000 },
        { label: "> 5000", min: 5000, max: Infinity },
      ];
  }
};

export const usePayrollUIConfig = () => {
  const { countryCode, loading: countryLoading } = usePayrollCountry();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadConfig = async () => {
      if (countryLoading) return;

      try {
        setLoading(true);
        setError(null);

        // ========================================
        // APPEL API BACKEND RÉEL
        // ========================================
        const response = await apiClient.get('v1/payroll/ui-config');

        // Enrichir la config avec des salary ranges basés sur la currency
        const enrichedConfig = {
          ...response.data,
          salaryRanges: generateSalaryRanges(response.data.currency),
        };

        setConfig(enrichedConfig);
      } catch (err) {
        console.error("Erreur chargement config payroll:", err);
        setError(err.message);

        // ========================================
        // FALLBACK TUNISIA EN CAS D'ERREUR
        // ========================================
        const fallbackConfig = {
          countryCode: "TN",
          currency: TUNISIA_DEFAULTS.currency, // TND
          locale: TUNISIA_DEFAULTS.locale, // fr-TN

          // Configuration temps de travail
          workingTime: {
            hoursPerMonth: 173.33, // 40h/semaine * 52/12
            daysPerMonth: 21.67, // 6 jours/semaine * 52/12
          },

          // Configuration ranges salaires
          salaryRanges: generateSalaryRanges(TUNISIA_DEFAULTS.currency),

          // Configuration overtime
          overtimeMultipliers: {
            normal: 1.25, // +25%
            night: 1.5, // +50%
            weekend: 2.0, // +100%
            holiday: 2.5, // +150%
          },

          // Configuration déductions par défaut
          deductionPolicies: {
            absence: "proportional_hourly",
            absenceRate: 1.0,
            lateness: "after_tolerance",
            latenessRate: 0.02,
            maxLatenessPercent: 10.0,
          },
        };

        setConfig(fallbackConfig);
      } finally {
        setLoading(false);
      }
    };

    loadConfig();
  }, [countryCode, countryLoading]);

  return {
    config,
    loading,
    error,
    countryCode,
    isReady: !loading && config && !error,
  };
};
